//LIBRARIES
import React, { useEffect, useState, useRef } from "react";
import { useParams, useLocation } from "react-router-dom";
import Twemoji from "react-twemoji";

import Loading from "./Loading";

//EFFECTS
import RandomText from "../effects/RandomText";

//DATA COMPONENTS
import ImageComponent from "../components/ImageComponent";
import VideoComponent from "../components/VideoComponent";
import BulletListComponent from "../components/BulletListComponent";
import DailyCC from "../components/DailyCC";
import BlogTitle from "../components/BlogTitle";

import Comment from "../components/Comment";
import ScrollProgressBar from "../components/ScrollProgressBar";
import SourceCodeComponent from "../components/SourceCodeComponent";
import LiveDiv from "../components/LiveDiv";
import HeadingToc from "../components/HeadingToc";

// Component to handle callout with children
function CalloutWithChildren({ calloutId, calloutText }) {
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCalloutChildren = async () => {
      try {
        const response = await fetch(`https://imajin.dev/api/retrieve-block-children?id=${calloutId}`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          throw new Error("Network response was not ok");
        }

        const result = await response.json();

        // console.log("children", result);

        setChildren(result.results);
      } catch (error) {
        // Error fetching callout children
      } finally {
        setLoading(false);
      }
    };

    fetchCalloutChildren();
  }, [calloutId]);

  // APPLY CALLOUT CODE TO STYLE
  const codeBlock = children.find(child => child.type === "code");

  let containerStyle = {};
  let styleCodeBlockId = null;
  
  if (codeBlock) {
    const codeText = codeBlock.code?.rich_text?.[0]?.plain_text || "";
    const trimmedText = codeText.trim();

    try {
      let jsonText = trimmedText.trim();
    
      jsonText = jsonText.replace(/(\w+)\s*:/g, '"$1":');
    
      if (!jsonText.startsWith('{')) {
        jsonText = `{${jsonText}}`;
      }
      
      // console.log("Cleaned JSON:", jsonText);
      containerStyle = JSON.parse(jsonText);
      styleCodeBlockId = codeBlock.id;
      console.log("containerStyle parsed:", containerStyle);
    } catch (error) {
      console.log("Error parsing container style JSON:", error.message);
    }
  }

  return (
    <div className="callout" style = { {  }}>
      <p style={{ margin: 0, marginBottom: children.length > 0 ? "8px" : 0 }}>{ calloutText }</p>
      {loading ? (
        <p style={{ margin: 0, fontSize: "12px", color: "var(--black72)" }}>Loading...</p>
      ) : (
        <div className = "child" style={{ ...containerStyle, background : "var(--black4)", display : "flex", flexDirection: "row"   }}>
          {children.map((child) => (
            <div key={child.id} style={{ marginTop: "8px" }}>
              {child.type === "paragraph" && (
                <p style={{ margin: 0 }}>
                  {child[child.type]?.rich_text?.map((text, idx) => text.plain_text).join("")}
                </p>
              )}
              {child.type === "image" && (
                <ImageComponent data={child[child.type]} />
              )}
              {child.type === "video" && (
                <VideoComponent data={child[child.type]} />
              )}
              {/* {child.type === "code" && child.id !== styleCodeBlockId && (
               <p>{ child[child.type]?.rich_text[0]?.plain_text }</p>
              )} */}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function BlogItem() {
  const { id } = useParams();
  const contentRef = useRef(null);

  const location = useLocation();

  const [loading ,setLoading] = useState(true);

  const [blog, setBlog] = useState([]);
  const [title, setTitle] = useState("");
  const [tags, setTags] = useState([]);
  const [series, setSeries] = useState("");
  const [created, setCreated] = useState("");

  const [bookLink, setBookLink] = useState("");

  const [data, setData] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);

    // CALLOUT COMPONENTS TO RENDER ACTUAL REACT COMPONENT
    // Auto-import all components from the ../graphics directory and map them by filename (without extension).
    // If require.context isn't available (some build setups), fall back to the explicit mapping above.
    let COMPONENTS = {};

    try {
      // webpack's require.context (works in CRA/Webpack environments)
      const req = require.context('../graphics', false, /\.jsx?$/);
      req.keys().forEach((key) => {
        // key looks like './HW1.jsx' or './Specular.jsx'
        const module = req(key);
        // prefer default export, otherwise take named export
        const comp = module && (module.default || module);
        if (!comp) return;
        // derive name from filename
        const name = key.replace(/^\.\/?/, '').replace(/\.jsx?$/, '');
        COMPONENTS[name] = comp;
      });
    } catch (e) {
      // Fallback: explicit mapping for environments without require.context
    }

    const resolveGraphicComponent = (rawName) => {
      const normalizedName = rawName
        .replace(/^graphics\//i, "")
        .replace(/\.jsx?$/i, "")
        .trim();

      const exactMatch = COMPONENTS[normalizedName];
      if (exactMatch) {
        return { name: normalizedName, component: exactMatch };
      }

      const lowerName = normalizedName.toLowerCase();
      const matchedKey = Object.keys(COMPONENTS).find((key) => key.toLowerCase() === lowerName);

      if (matchedKey) {
        return { name: matchedKey, component: COMPONENTS[matchedKey] };
      }

      return { name: normalizedName, component: null };
    };

  //TOGGLE BUTTON
  const [isChecked, setIsChecked] = useState(true);
  const handleToggle = () => {
    setIsChecked((prev) => !prev);
  };

  // FETCH BLOG
  const fetchBlog = async ( id ) => {
    try {
    const response = await fetch(`https://imajin.dev/api/retrieve-a-page?id=${id}`, {
        method: "GET",
        headers: {
        "Content-Type": "application/json",
        },
    });

    if (!response.ok) {
        throw new Error("Network response was not ok");
    }

    const result = await response.json();

    setBlog(result);

    setSeries(result.properties.Rollup?.rollup?.array[0].title[0].plain_text);
    setCreated(result.properties.Created_date.formula.string);
    
    setBookLink(result.properties.Link?.rich_text?.[0]?.plain_text);

    } catch (error) {
      // Error fetching data
    }
};

  // FETCH CONTENTS
  const fetchData = async () => {
    try {
      const response = await fetch(`https://imajin.dev/api/retrieve-block-children?id=${id}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error("Network response was not ok");
      }

      const responseData = await response.json();

      setData(responseData.results);
      setHasMore(responseData.has_more);
      setNextCursor(responseData.next_cursor);

      setLoading(false);
    } catch (error) {
      // Error fetching data
    }
  };

  // Function to fetch additional data (pagination)
  const fetchDataPagination = async (startCursor = null) => {
    try {
      let url = `https://imajin.dev/api/retrieve-block-children?id=${id}`;

      // If there's a cursor, append it to the URL as a query parameter
      if (startCursor) {
        url += `&start_cursor=${startCursor}`;
      }

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error("Network response was not ok");
      }

      const responseData = await response.json();

      // Append new data to existing data
      setData((prevData) => [...prevData, ...responseData.results]);
      setHasMore(responseData.has_more);
      setNextCursor(responseData.next_cursor);
    } catch (error) {
      // Error fetching additional data
    }
  };

  // Function to handle "Load More" button click
  const handleLoadMore = () => {
    if (nextCursor) {
      fetchDataPagination(nextCursor); // Fetch data with the next cursor
    }
  };
  
  useEffect(() => {
    fetchBlog(id);
  }, [id]); // Fetches blog when `id` changes.
  
  useEffect(() => {
    if (!blog) return;
  
    fetchData(); // contents
  
  }, [blog]);
  
  useEffect(() => {
    if (!blog || !data) return; // Ensure blog & data exist before accessing them
  
    setTitle(blog?.properties?.Name.title[0]?.plain_text || "");
    setTags(blog?.properties?.Tags?.multi_select || []);
  
  }, [data]);

  const isHeading = (type) => type === "heading_1" || type === "heading_2" || type === "heading_3";
  const isKorean = (str = "") => /[\uAC00-\uD7AF\u1100-\u11FF\u3130-\u318F]/.test(str);
    
  return (
    <div>

      <ScrollProgressBar/>

      { loading || !blog ? <Loading/> :
        <Twemoji options = { { className: 'twemoji' } } key = { location.pathname }>
          <div style = { { position : "relative", padding : 0 } }>

            <div>
              <div className = "verticalSeries" style = { { position : "fixed", left : 0, top : "72px", height : "160px", background : "", borderTop : "1px solid black",
              } }>
                <p style = { {position : "absolute", top : 0, margin : 0,
                  transformOrigin : "bottom left", color : "var(--black72)",
                  transform : "rotate(90deg)",
                  lineHeight : "100%",
                } }>{ series }</p>
              </div>

              <div className = "verticalSeries" style = { { position : "fixed", left : 0, top : "260px", height : "160px", background : "", borderTop : "1px solid black",
              } }>
                <p style = { {position : "absolute", top : 0, margin : 0,
                  transformOrigin : "bottom left", color : "var(--black72)",
                  transform : "rotate(90deg)",
                  lineHeight : "100%",
                  whiteSpace : "nowrap",
                } }>{ created }</p>
              </div>
            </div>

          
            <div className = "page" style = { { paddingLeft : "8px" } }>
              
              <BlogTitle data = { blog }/>
              
              <HeadingToc containerRef = { contentRef } maxLevel={3}/>

              {/* VERBOSE TOGGLE */}
              {/* <div style = { { display : "flex", marginBottom : "16px", alignItems: "center" } } >
                <p className = "meta2" style = { { marginRight : "8px" } }>Verbose Mode</p>
                <label className = "switch">
                  <input type = "checkbox" checked = { isChecked } onChange = { handleToggle } />
                  <span className = "slider"></span>
                </label>
              </div> */}   

              {/* PAGE */}
              { tags?.map(item => item.name).join(", ") === "Daily Creative Coding" && (
                <DailyCC data = { data } blog = { blog}/>
              )}

              { series === "52 Books" && (
                <div style = { { width : "100%", height : "360px", backgroundColor : "var(--black4)", display : "flex", alignItems : "center", justifyContent : "center", borderRadius : "4px" } }>
                  <img style = { { width: "auto", height : "80%" } } src = { bookLink }/>
                </div>
              )}
              
              
              {/* Render content */}
              <div ref = { contentRef }>
              { data.map( ( item ) => (
                <div key = { item.id } style={{ paddingLeft: isHeading(item.type) ? "0px" : "16px" }}>

                  { item.type === "callout" && (() => {
                    const calloutText = (item[item.type]?.rich_text || []).map((text) => text?.plain_text || "").join("");
                    const hasChildren = item.has_children;
                    
                    const { name: componentName, component: GraphicComponent } = resolveGraphicComponent(calloutText);

                    if (GraphicComponent) {
                      return (
                        <div>
                          <div style = { { display : "flex", flexDirection : "row", gap : "6px", alignItems : "center" } }>
                            <LiveDiv size={"8px"} color={"blue"}/>
                            <p className = "filepath">graphics/{ componentName }.jsx</p>
                          </div>

                          {
                            GraphicComponent &&
                              React.createElement(
                                GraphicComponent
                              )
                          }
                        </div>
                      );
                    }
                    
                    // Check if callout has children - fetch and render them
                    if (hasChildren) {
                      return <CalloutWithChildren calloutId={item.id} calloutText={calloutText} />;
                    }
                    
                    // Default callout rendering for simple cases
                    return (
                      <div className="callout" style={{ 
                        padding: "12px 16px", 
                        borderRadius: "4px", 
                        backgroundColor: "var(--black4)",
                        marginBottom: "16px"
                      }}>
                        <p style={{ margin: 0 }}>{ calloutText }</p>
                      </div>
                    );
                  })()}
                  
                  { item.type === "paragraph" && (
                    <div>
                      { item[item.type]?.rich_text?.[0]?.href ? (
                        <a className = "animated-underline" target = "_blank" href = { item[item.type]?.rich_text?.[0]?.href }>
                          { item[item.type]?.rich_text?.[0]?.plain_text }
                        </a>
                      ) : (
                        (() => {
                          const paraText = (item[item.type]?.rich_text || []).map(r => r?.plain_text || "").join("");
                          const korean = isKorean(paraText);
                          const paraColor = item[item.type]?.color === "black"
                            ? "var(--black)"
                            : item[item.type]?.color === "blue"
                            ? "var(--ptB)"
                            : undefined;
                          return (
                            <p
                              className = { korean ? "blogP-korean" : "blogP" }
                              style = { korean ? {} : { color: paraColor || "inherit" } }
                            >
                              { item[item.type]?.rich_text?.[0]?.plain_text }
                              <br />
                            </p>
                          );
                        })()
                      )}
                    </div>
                  )}

                  { item.type === "heading_1" && (
                    <h1 className="heading">{ item[item.type]?.rich_text[0]?.plain_text }</h1>
                  )}

                  { item.type === "heading_2" && (
                    <h2 className="heading">{ item[item.type]?.rich_text[0]?.plain_text }</h2>
                  )}

                  { item.type === "heading_3" && (
                    <h3 className="heading">{ item[item.type]?.rich_text[0]?.plain_text }</h3>
                  )}

                  { item.type === "divider" && <div className = "hr" /> }

                  { item.type === "bulleted_list_item" && (
                    <BulletListComponent item = { item } />
                  )}

                  { item.type === "image" && (
                    <ImageComponent data = { item[item.type] } />
                  )}

                  { item.type === "video" && tags?.map(item => item.name).join(", ") !== "Daily Creative Coding" && (
                    <VideoComponent data={item[item.type]}/>
                  )}

                  {/* {item.type === "video" && (
                    tags?.map(item => item.name).join(", ") === "Daily Creative Coding" ? (
                      <SourceCodeVideoComponent data={item[item.type]} />
                    ) : (
                      <VideoComponent data={item[item.type]} />
                    )
                  )} */}

                  { item.type === "code" && tags?.map(item => item.name).join(", ") !== "Daily Creative Coding" && (
                    <SourceCodeComponent data = { item[item.type] }/>
                  )}

                </div>
              ))}

              { hasMore && (
                <div>
                  <p className = "meta2">There are more.</p>
                  <button className = "moreButton" onClick = { handleLoadMore } style = { { width : "100%", height : "48px" } }>Load More</button>
                </div>
              )}
              </div>
            </div>
          </div>

          <Comment id = { id } blog = { title }/>
        </Twemoji>
      }
      

    </div>
  );
}
